import { inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { AuthState } from '@tsin-core/states/auth.state';
import { AuthService } from '@tsin-core/services/http/auth.service';

export type AppLocale = 'en' | 'fr';

@Injectable({ providedIn: 'root' })
export class LocaleService {
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly authService = inject(AuthService);

  private readonly _locale = signal<AppLocale>('en');

  /** Read-only signal for the active locale. */
  readonly locale = this._locale.asReadonly();

  /**
   * Initialises the locale from the current URL path.
   * Should be called once from the APP_INITIALIZER.
   */
  initFromUrl(): void {
    const segments = window.location.pathname.split('/').filter(Boolean);
    const locale: AppLocale = segments[0] === 'fr' ? 'fr' : 'en';
    this.applyLocale(locale);
  }

  /**
   * Switches the active locale and loads the corresponding translation file.
   * Does NOT navigate — callers are responsible for routing changes.
   */
  setLocale(locale: AppLocale): void {
    this.applyLocale(locale);
  }

  /**
   * Navigates to the equivalent page in the target locale.
   * Preserves the current path, query params, and fragment.
   * Persists the preference to the backend if the user is logged in.
   *
   * Navigue vers la page équivalente dans la langue cible.
   * Enregistre la préférence dans le backend si l'utilisateur est connecté.
   */
  switchLocale(target: AppLocale): void {
    const current = this._locale();
    if (current === target) return;

    const path = this.router.url; // e.g. /fr/user/PRO-I/steps/personal-information

    let newPath: string;
    if (target === 'fr') {
      // Add /fr prefix (strip leading slash first)
      newPath = '/fr' + (path.startsWith('/') ? path : '/' + path);
    } else {
      // Remove /fr prefix
      newPath = path.replace(/^\/fr(\/|$)/, '/');
      if (!newPath.startsWith('/')) newPath = '/' + newPath;
    }

    this.applyLocale(target);
    this.router.navigateByUrl(newPath);

    // Persist language preference to backend (MongoDB + Cognito) if logged in
    // Enregistrer la langue dans le backend si l'utilisateur est connecté
    const isAuthenticated = this.store.selectSnapshot(AuthState.getIsAuthenticated);
    if (isAuthenticated) {
      this.authService.updateLanguage(target).subscribe({
        error: (err: any) => console.warn('Failed to persist language preference:', err),
      });
    }
  }

  private applyLocale(locale: AppLocale): void {
    this._locale.set(locale);
    this.translate.use(locale);
    document.documentElement.lang = locale;
  }
}